import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, User, ArrowRight, X, Send } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

export default function AuthPage() {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ email: '', password: '', name: '' });
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handle = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'login') await login(form.email, form.password);
      else await register(form.email, form.name, form.password);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Something went wrong');
    } finally { setLoading(false); }
  };

  const sendReset = async () => {
    if (!forgotEmail || !forgotEmail.includes('@')) return toast.error('Enter a valid email address');
    setForgotLoading(true);
    try {
      await api.post('/api/auth/forgot-password', { email: forgotEmail });
      setForgotSent(true);
    } catch {
      toast.error('Failed to send reset email');
    } finally { setForgotLoading(false); }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', backgroundImage: 'radial-gradient(ellipse at 50% 0%, rgba(59,130,246,0.08) 0%, transparent 60%)' }}>
      <div style={{ width: '100%', maxWidth: 400, padding: '0 20px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{ width: 155, height: 95, borderRadius: 16, background: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14, overflow: 'hidden', boxShadow: '0 0 0 1px rgba(255,255,255,0.1), 0 4px 24px rgba(0,0,0,0.3)' }}>
            <img src="/logo.png" alt="Bulkify" style={{ width: 152, height: 90, objectFit: 'contain' }} onError={e => { e.target.parentElement.style.background = 'var(--blue)'; }} />
          </div>
          <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 4 }}>
            {mode === 'login' ? 'Sign in to your account' : 'Create your account'}
          </p>
        </div>

        <div className="card" style={{ padding: 28 }}>
          <form onSubmit={handle} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {mode === 'register' && (
              <div>
                <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>Full name</label>
                <div style={{ position: 'relative' }}>
                  <User size={14} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }} />
                  <input className="input" type="text" placeholder="John Doe" style={{ paddingLeft: 32 }} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
                </div>
              </div>
            )}
            <div>
              <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>Email</label>
              <div style={{ position: 'relative' }}>
                <Mail size={14} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }} />
                <input className="input" type="email" placeholder="you@example.com" style={{ paddingLeft: 32 }} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
              </div>
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <label style={{ fontSize: 12, color: 'var(--text2)' }}>Password</label>
                {mode === 'login' && (
                  <button type="button" onClick={() => setShowForgot(true)} style={{ background: 'none', border: 'none', color: 'var(--blue)', fontSize: 11, cursor: 'pointer' }}>
                    Forgot password?
                  </button>
                )}
              </div>
              <div style={{ position: 'relative' }}>
                <Lock size={14} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }} />
                <input className="input" type="password" placeholder="••••••••" style={{ paddingLeft: 32 }} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required minLength={6} />
              </div>
            </div>
            <button className="btn btn-primary" type="submit" disabled={loading} style={{ width: '100%', justifyContent: 'center', marginTop: 4, padding: '10px' }}>
              {loading ? 'Please wait…' : mode === 'login' ? 'Sign in' : 'Create account'}
              {!loading && <ArrowRight size={14} />}
            </button>
          </form>
        </div>

        <p style={{ textAlign: 'center', marginTop: 18, fontSize: 13, color: 'var(--text2)' }}>
          {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
          <button onClick={() => setMode(mode === 'login' ? 'register' : 'login')} style={{ background: 'none', border: 'none', color: 'var(--blue)', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
            {mode === 'login' ? 'Sign up' : 'Sign in'}
          </button>
        </p>
      </div>

      <div style={{ padding: '20px 0', textAlign: 'center', fontSize: 12, color: 'var(--text3)' }}>
        Built by{' '}
        <a href="https://x.com/meshcode2" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text2)', fontWeight: 500, textDecoration: 'none', borderBottom: '1px solid var(--border2)' }}
          onMouseOver={e => e.target.style.color='var(--blue)'} onMouseOut={e => e.target.style.color='var(--text2)'}>
          meshcode
        </a>
      </div>

      {/* Forgot password modal */}
      {showForgot && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div className="card" style={{ width: '100%', maxWidth: 380, padding: 28, position: 'relative' }}>
            <button onClick={() => { setShowForgot(false); setForgotSent(false); setForgotEmail(''); }} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer' }}>
              <X size={16} />
            </button>
            {forgotSent ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--green-dim)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                  <Send size={20} color="var(--green)" />
                </div>
                <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Reset email sent!</h2>
                <p style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>
                  Check your inbox at <strong>{forgotEmail}</strong> for the reset link. It expires in 1 hour.
                </p>
                <button className="btn btn-ghost" onClick={() => { setShowForgot(false); setForgotSent(false); }} style={{ marginTop: 18, width: '100%', justifyContent: 'center' }}>
                  Close
                </button>
              </div>
            ) : (
              <>
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                  <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--blue-dim)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                    <Lock size={18} color="#93c5fd" />
                  </div>
                  <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Forgot your password?</h2>
                  <p style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>Enter your email and we'll send you a reset link</p>
                </div>
                <div style={{ position: 'relative', marginBottom: 14 }}>
                  <Mail size={14} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }} />
                  <input className="input" type="email" placeholder="youremail@example.com" style={{ paddingLeft: 32 }} value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendReset()} />
                </div>
                <button className="btn btn-primary" onClick={sendReset} disabled={forgotLoading} style={{ width: '100%', justifyContent: 'center' }}>
                  {forgotLoading ? 'Sending…' : 'Send reset link'} {!forgotLoading && <ArrowRight size={14} />}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
